import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, Profile } from '../lib/supabase';
import { loginRateLimiter } from '../utils/rateLimiter';
import { clearSignedUrlCache } from '../utils/fileUrls';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  profileError: boolean;
  retryProfile: () => Promise<void>;
  passwordRecovery: boolean;
  clearPasswordRecovery: () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string, role: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  // Whose signed URLs are in the cache (see onAuthStateChange below).
  const signedInUserId = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState(false);
  const [passwordRecovery, setPasswordRecovery] = useState(
    // The recovery link lands with type=recovery in the URL hash before
    // supabase-js consumes it.
    () => window.location.hash.includes('type=recovery')
  );

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      (() => {
        if (event === 'PASSWORD_RECOVERY') {
          setPasswordRecovery(true);
        }
        // Signed URLs outlive the session and this SPA does not reload on
        // sign-out, so the next user in the tab would get the last one's links.
        // Keyed on the user, not on SIGNED_IN: Supabase emits SIGNED_IN on
        // every tab refocus, and clearing then re-signs every image on screen.
        const nextUserId = session?.user?.id ?? null;
        if (event === 'SIGNED_OUT' || nextUserId !== signedInUserId.current) {
          clearSignedUrlCache();
        }
        signedInUserId.current = nextUserId;
        setUser(session?.user ?? null);
        if (session?.user) {
          loadProfile(session.user.id);
        } else {
          setProfile(null);
          setLoading(false);
        }
      })();
    });

    return () => subscription.unsubscribe();
  }, []);

  function clearPasswordRecovery() {
    setPasswordRecovery(false);
  }

  async function loadProfile(userId: string) {
    setProfileError(false);
    try {
      // Race the query against a timeout so a hung request can't pin the
      // initial spinner forever.
      const fetchProfile = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Profile load timed out')), 15000)
      );

      const { data, error } = await Promise.race([fetchProfile, timeout]);

      if (error) throw error;
      // Force-log-out any session whose profile has been marked inactive.
      if (data && data.is_active === false) {
        await supabase.auth.signOut();
        setUser(null);
        setProfile(null);
        return;
      }
      setProfile(data);
    } catch (error) {
      // Surface a retryable error state instead of silently leaving profile
      // null (which would render the app as a degraded "no role" user).
      console.error('Error loading profile:', error);
      setProfileError(true);
    } finally {
      setLoading(false);
    }
  }

  async function retryProfile() {
    if (!user) return;
    setLoading(true);
    await loadProfile(user.id);
  }

  async function signIn(email: string, password: string) {
    // Check rate limit before attempting login
    const rateLimitCheck = loginRateLimiter.checkRateLimit(email.toLowerCase());
    
    if (!rateLimitCheck.allowed) {
      const lockedUntil = rateLimitCheck.lockedUntil;
      if (lockedUntil) {
        const minutes = Math.ceil((lockedUntil.getTime() - Date.now()) / 60000);
        throw new Error(`Too many failed login attempts. Account locked for ${minutes} minute(s).`);
      }
    }

    // Attempt login
    const { data: signInData, error } = await supabase.auth.signInWithPassword({ email, password });

    // Record the attempt
    loginRateLimiter.recordAttempt(email.toLowerCase(), !error);

    if (error) {
      const remaining = rateLimitCheck.remainingAttempts ? rateLimitCheck.remainingAttempts - 1 : 0;
      if (remaining > 0) {
        throw new Error(`${error.message}. ${remaining} attempt(s) remaining.`);
      } else {
        throw new Error(`${error.message}. Account will be locked after next failed attempt.`);
      }
    }

    // Block inactive users even though Supabase Auth accepted the password.
    if (signInData.user) {
      const { data: prof } = await supabase
        .from('profiles')
        .select('is_active')
        .eq('id', signInData.user.id)
        .maybeSingle();
      if (prof && prof.is_active === false) {
        await supabase.auth.signOut();
        throw new Error('Your account has been deactivated. Contact an administrator.');
      }
    }
  }

  async function signUp(email: string, password: string, fullName: string, role: string) {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;

    if (data.user) {
      const { error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: data.user.id,
          email,
          full_name: fullName,
          role,
          is_active: true
        });

      if (profileError) throw profileError;
    }
  }

  async function signOut() {
    console.log('AuthContext: signOut called');
    try {
      const { error } = await supabase.auth.signOut();
      console.log('AuthContext: signOut response', { error });
      if (error) {
        console.error('AuthContext: signOut error', error);
        throw error;
      }
      console.log('AuthContext: signOut successful');
    } catch (error) {
      console.error('AuthContext: signOut exception', error);
      throw error;
    }
  }

  return (
    <AuthContext.Provider value={{ user, profile, loading, profileError, retryProfile, passwordRecovery, clearPasswordRecovery, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
